import React from 'react';
import PropTypes from 'prop-types';
import { gql, useNavigate, useParams } from '@common/hooks';
import { useCurrentMembership } from '@identity/hooks';
import { Tabs } from '@common/components';
import { CandidateList } from './candidate-list';
import { OffersCandidateList } from './offers-candidate-list';

const CLOSED_STATE_LIST = ['Accepted', 'Withdrawn', 'Rejected'];

const STATE_TO_COUNT_MAP = {
  submittal_packet_change_requested:
    'submittedStaffingRequestApplicationsCount',
  Submitted: 'submittedStaffingRequestApplicationsCount',
  'In Review': 'inReviewStaffingRequestApplicationsCount',
  Offer: 'offeredOffersStaffingRequestApplicationsCount',
  Accepted: 'acceptedStaffingRequestApplicationsCount',
  Withdrawn: 'withdrawnStaffingRequestApplicationsCount',
  Rejected: 'rejectedStaffingRequestApplicationsCount',
};

export function CandidateTabs({ staffingRequest }) {
  const navigate = useNavigate();
  const applications = staffingRequest.staffingRequestApplications;
  const currentMembership = useCurrentMembership();

  let STATE_MAP = {
    in_review: 'In Review',
    offer: 'Offer',
    accepted: 'Accepted',
    withdrawn: 'Withdrawn',
    rejected: 'Rejected',
  };

  let STATE_LIST = ['In Review', 'Offer', 'Accepted', 'Withdrawn', 'Rejected'];

  let emptyStateMessage =
    'Sit tight, we’ll notify you when new shortlisted candidates for this Staffing Request are ready to review.';

  if (currentMembership.canShowApplicationsSubmitted) {
    STATE_MAP['submitted'] = 'Submitted';
    STATE_MAP['submittal_packet_change_requested'] = 'Submitted';
    emptyStateMessage =
      'Sit tight, we’ll notify you when there are candidates to review for this Staffing Request.';
    STATE_LIST.splice(0, 0, 'Submitted');
  }

  let stateBuckets = STATE_LIST.reduce((result, state) => {
    result[state] = {
      records: [],
      bucketCount: staffingRequest[STATE_TO_COUNT_MAP[state]],
    };
    return result;
  }, {});

  applications.forEach((sra) => {
    let stateBucketName = STATE_MAP[sra.state];
    if (!stateBucketName) return;
    stateBuckets[stateBucketName].records.push(sra);
  });

  const isStaffingRequestClosed =
    staffingRequest.state === 'FILLED' || staffingRequest.state === 'CANCELLED';
  const stateList = isStaffingRequestClosed ? CLOSED_STATE_LIST : STATE_LIST;

  function getCount(state) {
    return stateBuckets[state].bucketCount;
  }

  const { id: staffingRequestId, tabKey } = useParams();

  function updateUrlTabKey(key) {
    navigate(`/works/staffing-requests/${staffingRequestId}/${key}`, {
      replace: true,
    });
  }

  function getKeyByName(name) {
    return name.trim().toLowerCase().replaceAll(' ', '-');
  }

  return (
    <Tabs
      activeKey={tabKey || 'submitted'}
      onTabClick={(key) => updateUrlTabKey(key)}
    >
      {stateList.map((state) => {
        if (isStaffingRequestClosed && getCount(state) === 0) {
          return null;
        }

        const CandidateListComponent =
          state === 'Offer' ? OffersCandidateList : CandidateList;

        return (
          <Tabs.Pane
            tab={<Tabs.Tab title={state} count={getCount(state)} />}
            key={getKeyByName(state)}
          >
            <CandidateListComponent
              items={stateBuckets[state].records}
              emptyText={emptyStateMessage}
              staffingRequest={staffingRequest}
            />
          </Tabs.Pane>
        );
      })}
    </Tabs>
  );
}

CandidateTabs.fragments = {
  candidateList: gql`
    fragment StaffingRequestCandidateList on StaffingRequest {
      id
      state
      submittedStaffingRequestApplicationsCount
      inReviewStaffingRequestApplicationsCount
      offeredOffersStaffingRequestApplicationsCount
      acceptedStaffingRequestApplicationsCount
      withdrawnStaffingRequestApplicationsCount
      rejectedStaffingRequestApplicationsCount
      staffingRequestApplications {
        id
        state
        rejectionReason
        rejectionReasonDescription
        rejectionDetails
        supplier {
          id
          name
        }
        worksProfile {
          id
          firstName
          lastName
          fullName
          email
          phone
          profilePic
          profileSummary
        }
      }
    }
  `,
};

CandidateTabs.propTypes = {
  staffingRequest: PropTypes.object,
};
